import { identifyWithMetadata } from "@/helpers/intercom";
import { useInvestmentMetadataForIntercom } from "@/hooks/useIntercomMetadata";
import { useInvestment } from "@/hooks/useInvestments";
import { useOfferingAnalytics } from "@/hooks/useOfferingAnalytics";
import { useOfferingV2, usePlatformSettings } from "@/hooks/useOfferings";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { DTO } from "@equidefi/shared/types";
import { Dispatch, SetStateAction, createContext, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

type TWorkflowContext = {
  position: number;
  setPosition: Dispatch<SetStateAction<number>>;
  refetchInvestment: () => void;
  offering?: DTO.Offering;
  offeringError?: unknown;
  user?: DTO.User;
  investment?: DTO.Investment;
  analytics?: DTO.OfferingAnalytic;
};

export const WorkflowContext = createContext<TWorkflowContext>({
  position: 0,
  setPosition: () => {},
  refetchInvestment: () => {},
});

export const WorkflowProvider = ({ children }) => {
  const params = useParams();
  const { data: settings } = usePlatformSettings();
  const { slug, investment_id: investmentId } = params;

  const { data: user } = useCurrentUser();

  const { data: offering, error: offeringError } = useOfferingV2(slug);
  const { data: investment, isLoading: investmentIsLoading, refetch: refetchInvestment } = useInvestment(investmentId);

  const analytics = useOfferingAnalytics(offering, investment);

  // Collect investment metadata for Intercom
  const investmentMetadata = useInvestmentMetadataForIntercom(user, investment, offering);

  // Update Intercom with investment metadata when user enters workflow
  useEffect(() => {
    if (
      user?.intercom_hmac &&
      investment?.id &&
      offering?.id &&
      investmentMetadata &&
      Object.keys(investmentMetadata).length > 0
    ) {
      try {
        identifyWithMetadata(user, investmentMetadata);
      } catch (error) {
        console.error("Failed to update Intercom with investment metadata:", error);
      }
    }
  }, [user, investment, offering, investmentMetadata]);

  const startUri = useMemo(() => {
    const next = investment?.next;

    if (investmentIsLoading || !investment || !next) return null;

    // Don't try to route to these pages, they don't exist for the investor.
    if (["CLOSING", "REFUND", "COUNTERSIGN", "DILIGENCE"].includes(next)) {
      return null;
    }
    // Determine initial uri to load
    return next.toLowerCase();
  }, [investmentIsLoading, investment?.next, investment]);

  const [position, setPosition] = useState(1);

  return (
    <WorkflowContext.Provider
      value={{
        position,
        setPosition,
        user,
        offering,
        investment,
        analytics,
        refetchInvestment,
        startUri,
        investmentIsLoading,
        settings,
        offeringError,
      }}>
      {children}
    </WorkflowContext.Provider>
  );
};

export const useWorkflowContext = () => {
  return useContext(WorkflowContext);
};
